// Cómo se PINTA un adjunto: foto, PDF o audio.
//
// Asniel López (Pomares Insurance Group), 2026-09-11: *"por favor podrían
// habilitar en el área de TAREAS subir documentos PDF, ya que solo tenemos una
// opción para subir imágenes; sería de gran utilidad poder hacerlo más
// multimedia: subir pdf, llamadas, audios, etc."*.
//
// Las reglas de QUÉ se admite ya vivían en un solo sitio (`adjuntosNota.js`,
// abierto a PDF y audio el 2026-08-20 para las notas de póliza). Lo que no
// vivía en ninguno era cómo se ENSEÑA cada cosa, y ahí estaba el fallo de
// fondo: Tareas pintaba TODO dentro de un `<img>`. Un PDF en un `<img>` es un
// hueco roto que no dice qué es ni se puede abrir — así que aunque el servidor
// aceptaba el archivo desde siempre, subirlo no servía de nada.
//
// Por eso estas dos piezas son componentes y no un `if` dentro de cada
// pantalla: un audio se escucha AQUÍ (obligar a descargarlo para oír treinta
// segundos es la diferencia entre que se use y que no), y un PDF se abre con su
// nombre delante.
import { openBlobInNewTab, BlobImg, BlobLink, BlobAudio } from './BlobMedia.jsx';
import { tipoAdjunto, nombreAdjunto } from '../adjuntosNota.js';

const ICONO = { audio: '\u{1F3A7}', pdf: '\u{1F4C4}', archivo: '\u{1F4CE}' };

/**
 * La CASILLA CUADRADA de los editores: lo que se ve mientras se redacta, con su
 * botón de quitar.
 *
 * `adjunto` admite las dos formas que hay dando vueltas: la del formulario de
 * tarea ({ name, url, blobUrl, _preview }) y la de una nota ({ blobUrl,
 * _preview }). El nombre se busca en `name` y, si no está, se saca de la URL.
 */
export function MiniaturaAdjunto({ adjunto, lado = 74, onQuitar, lang = 'es' }) {
  const es = lang === 'es';
  const a = adjunto || {};
  const url = a.url || a.blobUrl || a._preview || '';
  const nombre = a.name || nombreAdjunto(url) || (es ? 'archivo' : 'file');
  // El tipo se decide por el NOMBRE cuando lo hay: una vista previa local
  // (`blob:…`) no tiene extensión, así que mirar solo la URL diría "archivo"
  // de una foto que se acaba de elegir.
  const tipo = tipoAdjunto(a.name || url);
  const marco = {
    position: 'relative', width: lado, height: lado, borderRadius: 10, overflow: 'hidden',
    border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)',
  };
  return (
    <div style={marco}>
      {tipo === 'imagen' ? (
        <BlobLink href={a.url || a._preview || a.blobUrl} target="_blank" rel="noreferrer" title={nombre}>
          <BlobImg src={a._preview || a.url || a.blobUrl} alt={nombre}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </BlobLink>
      ) : (
        <BlobLink href={a.url || a.blobUrl || a._preview} target="_blank" rel="noreferrer" title={nombre}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, width: '100%', height: '100%', textDecoration: 'none', color: 'var(--c-text2,#7aa3c0)', padding: 4, boxSizing: 'border-box' }}>
          <span style={{ fontSize: lado > 60 ? '1.4rem' : '1.1rem', lineHeight: 1 }}>{ICONO[tipo] || ICONO.archivo}</span>
          <span style={{ fontSize: '.56rem', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-all' }}>{nombre}</span>
        </BlobLink>
      )}
      {onQuitar && (
        <button type="button" onClick={onQuitar} title={es ? 'Quitar' : 'Remove'}
          style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.65)', color: '#fca5a5', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, fontSize: 11, lineHeight: 1 }}>
          ✕
        </button>
      )}
    </div>
  );
}

/**
 * El adjunto YA GUARDADO, tal como se lee: la foto se ve, el audio se escucha
 * sin salir de la pantalla, y el PDF (o cualquier otra cosa) es una ficha con
 * su nombre que se abre en otra pestaña.
 *
 * `url` es la firmada que devuelve el servidor; `BlobLink`/`BlobAudio` la
 * vuelven a firmar solos si caducó mientras la pestaña estaba abierta.
 */
export function AdjuntoGuardado({ url, nombre, lado = 84, lang = 'es' }) {
  const es = lang === 'es';
  const tipo = tipoAdjunto(nombre || url);
  const titulo = nombre || nombreAdjunto(url) || (es ? 'archivo' : 'file');

  if (tipo === 'audio') {
    return <BlobAudio src={url} controls preload="none" title={titulo}
      onClick={(e) => e.stopPropagation()} style={{ height: 34, maxWidth: 260 }} />;
  }
  if (tipo === 'imagen') {
    return (
      <BlobImg src={url} alt={titulo} title={es ? 'Abrir en grande' : 'Open full size'}
        onClick={(e) => { e.stopPropagation(); openBlobInNewTab(url); }}
        style={{ width: lado, height: lado, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--c-border2,#1f3f60)', cursor: 'pointer', display: 'block' }} />
    );
  }
  return (
    <BlobLink href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title={titulo}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '.4rem .6rem', borderRadius: 8,
        background: 'var(--c-surface,#0c1c2e)', border: '1px solid var(--c-border,#1a3550)',
        color: 'var(--c-text,#c8dff0)', textDecoration: 'none', fontSize: '.75rem', maxWidth: 240 }}>
      <span>{ICONO[tipo] || ICONO.archivo}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titulo}</span>
    </BlobLink>
  );
}
